import { useState, useEffect } from "react";

const STEPS = [
  {
    icon: "fa-duotone fa-cloud-arrow-up",
    title: "Upload",
    desc: "Drop a ZIP archive with your receivables data. Everette extracts, converts, and organises the files automatically.",
  },
  {
    icon: "fa-duotone fa-route",
    title: "Map Columns",
    desc: "Match your file columns to standard field names — Issue Date, Amount, Buyer, Document Type — so the engine can interpret the data.",
  },
  {
    icon: "fa-duotone fa-microchip",
    title: "Build the Master Frame",
    desc: "Merge all data groups into a single master dataset. Apply filters to exclude unwanted records.",
  },
  {
    icon: "fa-duotone fa-file-lines",
    title: "Classify & Rate",
    desc: "Map document types to rollforward categories and assign credit ratings to buyer parents.",
  },
  {
    icon: "fa-duotone fa-chart-mixed",
    title: "Analyse",
    desc: "Run the full analysis suite — aging, rollforward, delinquencies, dilution, concentration limits, and NERB — in one click.",
  },
  {
    icon: "fa-duotone fa-file-chart-column",
    title: "Report & Export",
    desc: "Generate an AI-powered executive report and download all outputs as CSV, Excel, or PowerPoint.",
  },
];

const WelcomeModal = ({ onStart }) => {
  const [show, setShow] = useState(false);
  const [stepsVisible, setStepsVisible] = useState(new Set());

  useEffect(() => {
    requestAnimationFrame(() => setShow(true));
    STEPS.forEach((_, i) => {
      setTimeout(() => {
        setStepsVisible((prev) => new Set([...prev, i]));
      }, 300 + i * 100);
    });
  }, []);

  const handleStart = () => {
    setShow(false);
    setTimeout(onStart, 350);
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 2000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: show ? "rgba(0,0,0,0.5)" : "rgba(0,0,0,0)",
        backdropFilter: show ? "blur(4px)" : "none",
        transition: "all 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      <div
        style={{
          width: "680px",
          maxWidth: "92vw",
          maxHeight: "90vh",
          borderRadius: "16px",
          backgroundColor: "var(--ev-white)",
          boxShadow: "0 24px 80px rgba(0,0,0,0.3)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          transform: show ? "scale(1) translateY(0)" : "scale(0.95) translateY(20px)",
          opacity: show ? 1 : 0,
          transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "2rem 2.5rem 1.5rem",
            background: "linear-gradient(135deg, #430098 0%, #5B2D8E 50%, #79589F 100%)",
            color: "#fff",
            textAlign: "center",
          }}
        >
          <p style={{ fontSize: "0.95rem", opacity: 0.85, lineHeight: 1.5, maxWidth: "480px", margin: "0 auto" }}>
            Trade receivables analysis from raw data to executive report in six steps.
          </p>
        </div>

        {/* Steps */}
        <div style={{ flex: 1, overflowY: "auto", padding: "1.5rem 2.5rem" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
            {STEPS.map((step, i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  gap: "0.85rem",
                  padding: "1rem",
                  borderRadius: "10px",
                  backgroundColor: "var(--ev-surface)",
                  border: "1px solid var(--ev-border)",
                  opacity: stepsVisible.has(i) ? 1 : 0,
                  transform: stepsVisible.has(i) ? "translateY(0)" : "translateY(12px)",
                  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                }}
              >
                <div style={{ position: "relative", flexShrink: 0 }}>
                  <div
                    style={{
                      width: "36px",
                      height: "36px",
                      borderRadius: "10px",
                      background: "linear-gradient(135deg, #430098, #79589F)",
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "1rem",
                    }}
                  >
                    <i className={step.icon} />
                  </div>
                  <div
                    style={{
                      position: "absolute",
                      top: "-6px",
                      right: "-6px",
                      width: "18px",
                      height: "18px",
                      borderRadius: "50%",
                      background: "#E8A735",
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "0.6rem",
                      fontWeight: 800,
                      boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
                    }}
                  >
                    {i + 1}
                  </div>
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: "0.88rem", color: "var(--ev-text)", marginBottom: "0.2rem" }}>
                    {step.title}
                  </div>
                  <div style={{ fontSize: "0.8rem", color: "var(--ev-text-muted)", lineHeight: 1.5 }}>
                    {step.desc}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Help hints */}
          <div
            style={{
              marginTop: "1.25rem",
              padding: "1rem 1.25rem",
              borderRadius: "10px",
              border: "1px solid var(--ev-border)",
              backgroundColor: "var(--ev-surface)",
              display: "flex",
              gap: "1.5rem",
              opacity: stepsVisible.has(5) ? 1 : 0,
              transform: stepsVisible.has(5) ? "translateY(0)" : "translateY(12px)",
              transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1) 0.15s",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flex: 1 }}>
              <div
                style={{
                  width: "28px",
                  height: "28px",
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, #430098, #79589F)",
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "0.75rem",
                  flexShrink: 0,
                }}
              >
                <i className="fa-solid fa-question" />
              </div>
              <span style={{ fontSize: "0.82rem", color: "var(--ev-text-muted)", lineHeight: 1.4 }}>
                Every screen has a <strong style={{ color: "var(--ev-text)" }}>help button</strong> in the tab bar with step-by-step guidance.
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flex: 1 }}>
              <div
                style={{
                  width: "28px",
                  height: "28px",
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, #430098, #79589F)",
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "0.75rem",
                  flexShrink: 0,
                }}
              >
                <i className="fa-duotone fa-message-bot" />
              </div>
              <span style={{ fontSize: "0.82rem", color: "var(--ev-text-muted)", lineHeight: 1.4 }}>
                Use the <strong style={{ color: "var(--ev-text)" }}>AI chat</strong> in the bottom-right corner to ask questions at any time.
              </span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            padding: "1.25rem 2.5rem",
            borderTop: "1px solid var(--ev-border)",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <button
            onClick={handleStart}
            style={{
              padding: "0.75rem 2.5rem",
              borderRadius: "10px",
              border: "none",
              background: "linear-gradient(135deg, #430098 0%, #79589F 100%)",
              color: "#fff",
              fontWeight: 700,
              fontSize: "1rem",
              cursor: "pointer",
              boxShadow: "0 4px 16px rgba(67, 0, 152, 0.35)",
              transition: "all 0.2s ease",
              display: "flex",
              alignItems: "center",
              gap: "0.6rem",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-1px)";
              e.currentTarget.style.boxShadow = "0 6px 24px rgba(67, 0, 152, 0.45)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 4px 16px rgba(67, 0, 152, 0.35)";
            }}
          >
            Start Analysis
            <i className="fa-solid fa-arrow-right" style={{ fontSize: "0.85rem" }} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default WelcomeModal;
